import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui'

interface ContentWarningProps {
  message: string
  explanation?: string
  onReview: () => void
  onProceed: () => void
  isLoading?: boolean
}

/**
 * Kid-friendly content warning dialog.
 * Shows when content safety check flags potential issues.
 * Allows kids to either review their work or proceed anyway.
 */
export function ContentWarning({
  message,
  explanation,
  onReview,
  onProceed,
  isLoading = false,
}: ContentWarningProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-[var(--radius-kid-lg)] shadow-[var(--shadow-kid-lg)] max-w-md w-full p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--color-kids-warning)]/20 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-[var(--color-kids-warning-dark)]" />
        </div>

        <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-3">
          Let's Take Another Look
        </h2>

        <p className="text-[var(--color-kids-text-muted)] mb-4">{message}</p>

        {explanation && (
          <p className="text-sm text-[var(--color-kids-text-muted)] mb-6 bg-[var(--color-kids-surface-alt)] p-3 rounded-[var(--radius-kid)]">
            {explanation}
          </p>
        )}

        <div className="flex gap-3">
          <Button variant="ghost" onClick={onReview} className="flex-1">
            Review My Story
          </Button>
          <Button
            variant="primary"
            onClick={onProceed}
            isLoading={isLoading}
            className="flex-1"
          >
            Submit Anyway
          </Button>
        </div>
      </div>
    </div>
  )
}
